/**
 * Control de Labores · Vista general (2026-10-09): para el coordinador de Mano
 * de Obra y el jefe de SSCC. Mapa con cada punto según cómo va (completo, a
 * medias, pendiente) y dónde se tomó cada evidencia; resumen por supervisor y
 * por trabajador.
 */
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { rec_React as React, rec_api as api } from "../../runtime/index.jsx";

const useEffect = (...a) => React.useEffect(...a);
const useRef = (...a) => React.useRef(...a);
const useState = (...a) => React.useState(...a);

const COLOR = { completo: `#54904C`, parcial: `#DB9647`, pendiente: `#8A95A3`, fuera: `#D7594E`, libre: `#B5BEC8` };
const hora = (v) => (v ? new Date(v).toLocaleTimeString(`es-PE`, { hour: `2-digit`, minute: `2-digit` }) : `—`);
const oscuro = () => {
  const t = document.documentElement.dataset.theme;
  return t ? t === `dark` : window.matchMedia?.(`(prefers-color-scheme: dark)`).matches;
};

/** Junta asignaciones y puntos en filas por punto asignado. */
function filas(dia, puntos) {
  const out = [];
  for (const a of (dia?.asignaciones ?? []).filter((x) => x.estado === `ACTIVA`)) {
    for (const ap of a.puntos) {
      const p = puntos.find((x) => x.id === ap.puntoId);
      const hechas = Math.min(ap.pasadas, ap.evidencias.length);
      const fuera = ap.evidencias.filter((e) => e.distanciaM != null && p && e.distanciaM > p.radioM).length;
      out.push({
        a, ap, p, hechas, fuera,
        estado: hechas >= ap.pasadas ? `completo` : hechas > 0 ? `parcial` : `pendiente`,
        ultima: ap.evidencias.at(-1)?.tomadaEn ?? null,
      });
    }
  }
  return out;
}

const PERIODOS = [
  { dias: 1, texto: `Día` },
  { dias: 7, texto: `7 días` },
  { dias: 30, texto: `30 días` },
  { dias: 60, texto: `60 días` },
];
const restar = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export function VistaGeneral({ dia, puntos, fecha }) {
  const [periodo, setPeriodo] = useState(1);
  return (
    <div className="lb-general">
      <div className="lb-filtros">
        <span className="lb-sub">Período:</span>
        {PERIODOS.map((p) => (
          <button key={p.dias} className={periodo === p.dias ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setPeriodo(p.dias)}>{p.texto}</button>
        ))}
        {periodo > 1 && <span className="lb-sub">del {restar(fecha, periodo - 1).split(`-`).reverse().join(`/`)} al {fecha.split(`-`).reverse().join(`/`)}</span>}
      </div>
      {periodo === 1 ? <VistaDia dia={dia} puntos={puntos} /> : <VistaPeriodo desde={restar(fecha, periodo - 1)} hasta={fecha} />}
    </div>
  );
}

function VistaDia({ dia, puntos }) {
  const [fundo, setFundo] = useState(`TODOS`);
  const [supervisor, setSupervisor] = useState(``);
  if (!dia) return <p className="lb-vacio">Cargando…</p>;
  const todas = filas(dia, puntos);
  const fundos = [`TODOS`, ...new Set(puntos.map((p) => p.fundo))];
  const supervisores = [...new Map(todas.map((f) => [String(f.a.supervisorId ?? ``), f.a.supervisor ?? `—`]))].sort((x, y) => x[1].localeCompare(y[1]));
  const vis = todas.filter((f) => (fundo === `TODOS` || f.p?.fundo === fundo) && (!supervisor || String(f.a.supervisorId ?? ``) === supervisor));
  const porFundo = new Map();
  for (const f of vis) {
    const k = f.p?.fundo ?? `—`;
    const r = porFundo.get(k) ?? { nombre: k, puntos: 0, completos: 0, pedidas: 0, hechas: 0, fuera: 0, trab: new Set() };
    r.puntos++; r.completos += f.estado === `completo` ? 1 : 0; r.pedidas += f.ap.pasadas; r.hechas += f.hechas; r.fuera += f.fuera; r.trab.add(f.a.trabajadorId);
    porFundo.set(k, r);
  }
  const fundosOrden = [...porFundo.values()].sort((x, y) => x.hechas / x.pedidas - y.hechas / y.pedidas);
  const pedidas = vis.reduce((s, f) => s + f.ap.pasadas, 0);
  const hechas = vis.reduce((s, f) => s + f.hechas, 0);
  const pct = pedidas ? Math.round((100 * hechas) / pedidas) : 0;

  // Por trabajador y por supervisor
  const porTrab = new Map();
  for (const f of vis) {
    const k = f.a.trabajadorId;
    const r = porTrab.get(k) ?? { nombre: f.a.trabajador, supervisor: f.a.supervisor ?? `—`, puntos: 0, completos: 0, pedidas: 0, hechas: 0, fuera: 0, ultima: null };
    r.puntos++; r.completos += f.estado === `completo` ? 1 : 0; r.pedidas += f.ap.pasadas; r.hechas += f.hechas; r.fuera += f.fuera;
    if (f.ultima && (!r.ultima || f.ultima > r.ultima)) r.ultima = f.ultima;
    porTrab.set(k, r);
  }
  const trabajadores = [...porTrab.values()].sort((x, y) => x.hechas / x.pedidas - y.hechas / y.pedidas);
  const porSup = new Map();
  for (const t of trabajadores) {
    const r = porSup.get(t.supervisor) ?? { nombre: t.supervisor, trabajadores: 0, pedidas: 0, hechas: 0, fuera: 0 };
    r.trabajadores++; r.pedidas += t.pedidas; r.hechas += t.hechas; r.fuera += t.fuera;
    porSup.set(t.supervisor, r);
  }

  return (
    <div className="lb-general">
      <div className="lb-filtros">
        {fundos.map((f) => (
          <button key={f} className={fundo === f ? `lb-filtro lb-filtro-on` : `lb-filtro`} onClick={() => setFundo(f)}>
            {f === `TODOS` ? `Todos los fundos` : f}
          </button>
        ))}
        {supervisores.length > 1 && (
          <select className="lb-filtro" value={supervisor} onChange={(e) => setSupervisor(e.target.value)} aria-label="Supervisor">
            <option value="">Todos los supervisores</option>
            {supervisores.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
        )}
      </div>
      <div className="lb-resumen">
        <div className="lb-cifra"><span className="lb-cifra-valor">{pct}%</span><span className="lb-cifra-etiqueta">avance ({hechas} de {pedidas} pasadas)</span><span className="lb-barra"><i style={{ width: `${pct}%` }} /></span></div>
        <div className="lb-cifra"><span className="lb-cifra-valor">{vis.filter((f) => f.estado === `completo`).length} / {vis.length}</span><span className="lb-cifra-etiqueta">puntos completos</span></div>
        <div className="lb-cifra"><span className="lb-cifra-valor">{vis.filter((f) => f.estado === `pendiente`).length}</span><span className="lb-cifra-etiqueta">puntos sin ninguna pasada</span></div>
        <div className={vis.some((f) => f.fuera) ? `lb-cifra lb-cifra-alerta` : `lb-cifra`}><span className="lb-cifra-valor">{vis.reduce((s, f) => s + f.fuera, 0)}</span><span className="lb-cifra-etiqueta">evidencias fuera del punto</span></div>
        <div className="lb-cifra"><span className="lb-cifra-valor">{porTrab.size}</span><span className="lb-cifra-etiqueta">trabajadores en campo</span></div>
      </div>
      <div className="lb-general-cuerpo">
        <div className="lb-mapa-caja">
          <Mapa filas={vis} puntos={fundo === `TODOS` ? puntos : puntos.filter((p) => p.fundo === fundo)} />
          <div className="lb-leyenda">
            <span><i style={{ background: COLOR.completo }} />Completo</span>
            <span><i style={{ background: COLOR.parcial }} />A medias</span>
            <span><i style={{ background: COLOR.pendiente }} />Sin pasadas</span>
            <span><i className="lb-leyenda-hueco" />Sin asignar hoy</span>
            <span><i className="lb-leyenda-punto" style={{ background: COLOR.fuera }} />Evidencia fuera del punto</span>
          </div>
        </div>
        <div className="lb-tablas">
          <section className="lb-bloque">
            <h3>Por fundo</h3>
            <div className="lb-tabla-caja">
              <table className="lb-tabla">
                <thead><tr><th>Fundo</th><th>Trab.</th><th>Puntos</th><th>Avance</th><th>Fuera</th></tr></thead>
                <tbody>
                  {fundosOrden.map((x) => (
                    <tr key={x.nombre}><td>{x.nombre}</td><td>{x.trab.size}</td><td>{x.completos}/{x.puntos}</td><td><Avance h={x.hechas} p={x.pedidas} /></td><td className={x.fuera ? `lb-mal` : ``}>{x.fuera}</td></tr>
                  ))}
                  {fundosOrden.length === 0 && <tr><td colSpan={5} className="lb-sub">Nada asignado.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
          <section className="lb-bloque">
            <h3>Por supervisor</h3>
            <div className="lb-tabla-caja">
              <table className="lb-tabla">
                <thead><tr><th>Supervisor</th><th>Trab.</th><th>Avance</th><th>Fuera</th></tr></thead>
                <tbody>
                  {[...porSup.values()].sort((x, y) => x.hechas / x.pedidas - y.hechas / y.pedidas).map((s) => (
                    <tr key={s.nombre}><td>{s.nombre}</td><td>{s.trabajadores}</td><td><Avance h={s.hechas} p={s.pedidas} /></td><td className={s.fuera ? `lb-mal` : ``}>{s.fuera}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className="lb-bloque">
            <h3>Trabajadores más atrasados</h3>
            <div className="lb-tabla-caja">
              <table className="lb-tabla">
                <thead><tr><th>Trabajador</th><th>Puntos</th><th>Avance</th><th>Fuera</th><th>Última</th></tr></thead>
                <tbody>
                  {trabajadores.slice(0, 15).map((t) => (
                    <tr key={t.nombre}>
                      <td>{t.nombre}<span className="lb-sub"> · {t.supervisor}</span></td>
                      <td>{t.completos}/{t.puntos}</td>
                      <td><Avance h={t.hechas} p={t.pedidas} /></td>
                      <td className={t.fuera ? `lb-mal` : ``}>{t.fuera}</td>
                      <td>{hora(t.ultima)}</td>
                    </tr>
                  ))}
                  {trabajadores.length === 0 && <tr><td colSpan={5} className="lb-sub">Nadie asignado este día.</td></tr>}
                  {trabajadores.length > 15 && <tr><td colSpan={5} className="lb-sub">Y {trabajadores.length - 15} más. El detalle de cada uno está en «Avance del día».</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Avance({ h, p, meta }) {
  const pct = p ? Math.round((100 * h) / p) : 0;
  // En un día en curso, a medias es naranja; en un período, se mide contra la meta de 90%.
  const color = meta ? colorPct(pct) : pct >= 100 ? COLOR.completo : pct > 0 ? COLOR.parcial : COLOR.pendiente;
  return (
    <span className="lb-avance-celda">
      <span className="lb-barra"><i style={{ width: `${pct}%`, background: color }} /></span>
      <b>{pct}%</b>
    </span>
  );
}

function Mapa({ filas, puntos }) {
  const caja = useRef(null);
  const mapa = useRef(null);
  const capa = useRef(null);

  useEffect(() => {
    const m = L.map(caja.current, { zoomControl: true, attributionControl: true });
    // Mapas de Esri (sin clave): gris claro/oscuro y satélite para ver los fundos.
    const esri = (servicio) => `https://server.arcgisonline.com/ArcGIS/rest/services/${servicio}/MapServer/tile/{z}/{y}/{x}`;
    const base = L.tileLayer(esri(oscuro() ? `Canvas/World_Dark_Gray_Base` : `Canvas/World_Light_Gray_Base`), {
      maxNativeZoom: 16, maxZoom: 19, attribution: `© Esri`,
    });
    const satelite = L.tileLayer(esri(`World_Imagery`), { maxNativeZoom: 18, maxZoom: 19, attribution: `© Esri, Maxar` });
    base.addTo(m);
    L.control.layers({ Mapa: base, Satélite: satelite }, null, { position: `topright` }).addTo(m);
    capa.current = L.layerGroup().addTo(m);
    m.setView([-14.0665, -75.7285], 16);
    mapa.current = m;
    return () => m.remove();
  }, []);

  useEffect(() => {
    const g = capa.current;
    if (!g) return;
    g.clearLayers();
    const asignados = new Set(filas.map((f) => f.ap.puntoId));
    const limites = [];
    for (const p of puntos) {
      if (p.lat == null || asignados.has(p.id)) continue;
      L.circleMarker([p.lat, p.lng], { radius: 7, color: COLOR.libre, weight: 2, fillOpacity: 0 })
        .bindTooltip(`${p.codigo} · sin asignar hoy`).addTo(g);
      limites.push([p.lat, p.lng]);
    }
    for (const f of filas) {
      if (!f.p || f.p.lat == null) continue;
      const c = COLOR[f.estado];
      L.circle([f.p.lat, f.p.lng], { radius: f.p.radioM, color: c, weight: 1, fillOpacity: 0.08 }).addTo(g);
      L.circleMarker([f.p.lat, f.p.lng], { radius: 10, color: `#fff`, weight: 2, fillColor: c, fillOpacity: 1 })
        .bindPopup(`<b>${f.p.codigo}</b> ${f.p.nombre}<br>${f.a.trabajador} · ${f.hechas}/${f.ap.pasadas} pasadas<br>Última: ${hora(f.ultima)}${f.fuera ? `<br><span style="color:${COLOR.fuera}">${f.fuera} fuera del punto</span>` : ``}`)
        // Con muchos puntos las etiquetas fijas se tapan entre sí: se ven al pasar el mouse.
        .bindTooltip(`${f.p.codigo} ${f.hechas}/${f.ap.pasadas}`, { permanent: filas.length <= 25, direction: `top`, offset: [0, -10], className: `lb-etiqueta-mapa` })
        .addTo(g);
      limites.push([f.p.lat, f.p.lng]);
      for (const e of f.ap.evidencias) {
        if (e.lat == null) continue;
        const lejos = e.distanciaM != null && e.distanciaM > f.p.radioM;
        if (lejos) L.polyline([[f.p.lat, f.p.lng], [e.lat, e.lng]], { color: COLOR.fuera, weight: 1.5, dashArray: `4 4` }).addTo(g);
        L.circleMarker([e.lat, e.lng], { radius: 4, color: lejos ? COLOR.fuera : c, weight: 1, fillOpacity: 0.9 })
          .bindTooltip(`${f.p.codigo} · ${hora(e.tomadaEn)}${lejos ? ` · a ${e.distanciaM} m` : ``}`).addTo(g);
        if (lejos) limites.push([e.lat, e.lng]);
      }
    }
    if (limites.length && mapa.current) mapa.current.fitBounds(limites, { padding: [30, 30], maxZoom: 17 });
  }, [filas, puntos]);

  return <div ref={caja} className="lb-mapa" />;
}


// ── Varios días ──

const pctDe = (h, p) => (p ? Math.round((100 * h) / p) : 0);
const colorPct = (pct) => (pct >= 90 ? COLOR.completo : pct >= 70 ? COLOR.parcial : COLOR.fuera);

function VistaPeriodo({ desde, hasta }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(``);
  useEffect(() => {
    setDatos(null);
    api(`/labores/periodo?desde=${desde}&hasta=${hasta}`, { sinCache: true }).then(setDatos).catch((e) => setError(e.message));
  }, [desde, hasta]);
  if (error) return <p className="lb-error">{error}</p>;
  if (!datos) return <p className="lb-vacio">Cargando…</p>;
  const tot = datos.dias.reduce((s, d) => ({ p: s.p + d.pedidas, h: s.h + d.hechas, f: s.f + d.fuera, t: s.t + d.tarde }), { p: 0, h: 0, f: 0, t: 0 });
  const pct = pctDe(tot.h, tot.p);
  const ev = Math.max(1, tot.h);
  const peor = [...datos.puntos].sort((x, y) => pctDe(x.hechas, x.pedidas) - pctDe(y.hechas, y.pedidas)).slice(0, 5);
  return (
    <>
      <div className="lb-resumen">
        <div className="lb-cifra"><span className="lb-cifra-valor" style={{ color: colorPct(pct) }}>{pct}%</span><span className="lb-cifra-etiqueta">cumplimiento ({tot.h} de {tot.p} pasadas)</span><span className="lb-barra"><i style={{ width: `${pct}%` }} /></span></div>
        <div className="lb-cifra"><span className="lb-cifra-valor">{datos.dias.length}</span><span className="lb-cifra-etiqueta">días con labor</span></div>
        <div className={tot.f ? `lb-cifra lb-cifra-alerta` : `lb-cifra`}><span className="lb-cifra-valor">{tot.f}</span><span className="lb-cifra-etiqueta">fuera del punto ({Math.round((100 * tot.f) / ev)}% de evidencias)</span></div>
        <div className="lb-cifra"><span className="lb-cifra-valor">{tot.t}</span><span className="lb-cifra-etiqueta">llegaron tarde por falta de señal ({Math.round((100 * tot.t) / ev)}%)</span></div>
        <div className="lb-cifra"><span className="lb-cifra-valor">{datos.trabajadores.length}</span><span className="lb-cifra-etiqueta">trabajadores en el período</span></div>
      </div>
      <section className="lb-bloque">
        <h3>Cumplimiento día a día</h3>
        <Tendencia dias={datos.dias} />
      </section>
      <div className="lb-general-cuerpo">
        <div className="lb-mapa-caja">
          <MapaPeriodo puntos={datos.puntos} />
          <div className="lb-leyenda">
            <span><i style={{ background: COLOR.completo }} />90% o más</span>
            <span><i style={{ background: COLOR.parcial }} />70 a 89%</span>
            <span><i style={{ background: COLOR.fuera }} />Menos de 70%</span>
            <span>El tamaño del punto es cuántas pasadas se pidieron.</span>
          </div>
          <section className="lb-bloque">
            <h3>Puntos con menor cumplimiento</h3>
            <div className="lb-tabla-caja">
              <table className="lb-tabla">
                <thead><tr><th>Punto</th><th>Fundo</th><th>Cumplimiento</th><th>Fuera</th></tr></thead>
                <tbody>{peor.map((p) => <tr key={p.puntoId}><td>{p.codigo} <span className="lb-sub">{p.nombre}</span></td><td>{p.fundo}</td><td><Avance h={p.hechas} p={p.pedidas} meta /></td><td className={p.fuera ? `lb-mal` : ``}>{p.fuera}</td></tr>)}</tbody>
              </table>
            </div>
          </section>
        </div>
        <div className="lb-tablas">
          <TablaPeriodo titulo="Por fundo" filas={fundosDe(datos.puntos)} extra="Puntos" sinTarde />
          <TablaPeriodo titulo="Por supervisor" filas={datos.supervisores} extra="Trab." />
          <TablaPeriodo titulo="Por trabajador" filas={[...datos.trabajadores].sort((x, y) => pctDe(x.hechas, x.pedidas) - pctDe(y.hechas, y.pedidas))} extra="Supervisor" limite={20} />
        </div>
      </div>
    </>
  );
}

/** Junta los puntos del período por fundo (los días son el mayor de sus puntos). */
function fundosDe(puntos) {
  const m = new Map();
  for (const p of puntos) {
    const k = p.fundo ?? `—`;
    const r = m.get(k) ?? { id: k, nombre: k, extra: 0, dias: 0, pedidas: 0, hechas: 0, fuera: 0, tarde: 0 };
    r.extra++; r.dias = Math.max(r.dias, p.dias); r.pedidas += p.pedidas; r.hechas += p.hechas; r.fuera += p.fuera;
    m.set(k, r);
  }
  return [...m.values()].sort((x, y) => pctDe(x.hechas, x.pedidas) - pctDe(y.hechas, y.pedidas));
}

function TablaPeriodo({ titulo, filas, extra, limite, sinTarde }) {
  const [todos, setTodos] = useState(false);
  const vis = limite && !todos ? filas.slice(0, limite) : filas;
  return (
    <section className="lb-bloque">
      <h3>{titulo}</h3>
      <div className="lb-tabla-caja">
        <table className="lb-tabla">
          <thead><tr><th>{titulo.replace(`Por `, ``)}</th><th>{extra}</th><th>Días</th><th>Cumplimiento</th><th>Fuera</th>{!sinTarde && <th>Tarde</th>}</tr></thead>
          <tbody>
            {vis.map((f) => (
              <tr key={`${f.id}-${f.nombre}`}>
                <td>{f.nombre}</td><td className="lb-sub">{f.extra}</td><td>{f.dias}</td>
                <td><Avance h={f.hechas} p={f.pedidas} meta /></td>
                <td className={f.fuera ? `lb-mal` : ``}>{f.fuera}</td>{!sinTarde && <td>{f.tarde}</td>}
              </tr>
            ))}
            {filas.length === 0 && <tr><td colSpan={6} className="lb-sub">Sin datos en el período.</td></tr>}
            {limite && filas.length > limite && (
              <tr><td colSpan={6}><button className="lb-filtro" onClick={() => setTodos(!todos)}>{todos ? `Ver sólo los ${limite} más bajos` : `Ver los ${filas.length}`}</button></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Barras por día con la meta de 90%. */
function Tendencia({ dias }) {
  if (dias.length === 0) return <p className="lb-vacio">Sin labores en el período.</p>;
  const W = Math.max(600, dias.length * 18), H = 170, top = 12, bot = 26, alto = H - top - bot;
  const ancho = W / dias.length;
  const y = (pct) => top + alto * (1 - pct / 100);
  return (
    <div className="lb-tendencia">
      <svg viewBox={`0 0 ${W + 34} ${H}`} preserveAspectRatio="none" role="img" aria-label="Cumplimiento diario">
        {[0, 50, 100].map((v) => (
          <g key={v}>
            <line x1={30} x2={W + 30} y1={y(v)} y2={y(v)} className="lb-eje" />
            <text x={26} y={y(v) + 4} textAnchor="end" className="lb-eje-txt">{v}%</text>
          </g>
        ))}
        <line x1={30} x2={W + 30} y1={y(90)} y2={y(90)} className="lb-meta" />
        <text x={W + 30} y={y(90) - 4} textAnchor="end" className="lb-meta-txt">meta 90%</text>
        {dias.map((d, i) => {
          const pct = pctDe(d.hechas, d.pedidas);
          const x = 30 + i * ancho + ancho * 0.18;
          return (
            <g key={d.fecha}>
              <rect x={x} y={y(pct)} width={ancho * 0.64} height={Math.max(1, top + alto - y(pct))} rx={2} fill={colorPct(pct)}>
                <title>{`${d.fecha.split(`-`).reverse().join(`/`)}: ${pct}% (${d.hechas}/${d.pedidas})${d.fuera ? ` · ${d.fuera} fuera` : ``}`}</title>
              </rect>
              {(i % Math.ceil(dias.length / 10) === 0) && <text x={x + ancho * 0.32} y={H - 8} textAnchor="middle" className="lb-eje-txt">{d.fecha.slice(8)}/{d.fecha.slice(5, 7)}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function MapaPeriodo({ puntos }) {
  const caja = useRef(null);
  useEffect(() => {
    const m = L.map(caja.current);
    const esri = (s) => `https://server.arcgisonline.com/ArcGIS/rest/services/${s}/MapServer/tile/{z}/{y}/{x}`;
    const base = L.tileLayer(esri(oscuro() ? `Canvas/World_Dark_Gray_Base` : `Canvas/World_Light_Gray_Base`), { maxNativeZoom: 16, maxZoom: 19, attribution: `© Esri` }).addTo(m);
    const sat = L.tileLayer(esri(`World_Imagery`), { maxNativeZoom: 18, maxZoom: 19, attribution: `© Esri, Maxar` });
    L.control.layers({ Mapa: base, Satélite: sat }, null, { position: `topright` }).addTo(m);
    const lim = [];
    const maxP = Math.max(1, ...puntos.map((p) => p.pedidas));
    for (const p of puntos) {
      if (p.lat == null) continue;
      const pct = pctDe(p.hechas, p.pedidas);
      L.circleMarker([p.lat, p.lng], { radius: 8 + 10 * (p.pedidas / maxP), color: `#fff`, weight: 2, fillColor: colorPct(pct), fillOpacity: 0.9 })
        .bindPopup(`<b>${p.codigo}</b> ${p.nombre}<br>${pct}% · ${p.hechas} de ${p.pedidas} pasadas en ${p.dias} días${p.fuera ? `<br><span style="color:${COLOR.fuera}">${p.fuera} fuera del punto</span>` : ``}`)
        .bindTooltip(`${p.codigo} ${pct}%`, { permanent: puntos.length <= 25, direction: `top`, offset: [0, -12], className: `lb-etiqueta-mapa` })
        .addTo(m);
      lim.push([p.lat, p.lng]);
    }
    if (lim.length) m.fitBounds(lim, { padding: [40, 40], maxZoom: 17 }); else m.setView([-14.0665, -75.7285], 15);
    return () => m.remove();
  }, [puntos]);
  return <div ref={caja} className="lb-mapa" />;
}

// ── Mapa de puntos editable (coordinador) ──

/** Puntos sobre el mapa; con permiso, se arrastran para corregir la ubicación (se guarda al soltar). */
export function MapaPuntos({ puntos, editable, onCambio }) {
  const caja = useRef(null);
  const [aviso, setAviso] = useState(``);
  useEffect(() => {
    const m = L.map(caja.current);
    const esri = (s) => `https://server.arcgisonline.com/ArcGIS/rest/services/${s}/MapServer/tile/{z}/{y}/{x}`;
    const sat = L.tileLayer(esri(`World_Imagery`), { maxNativeZoom: 18, maxZoom: 19, attribution: `© Esri, Maxar` });
    const base = L.tileLayer(esri(oscuro() ? `Canvas/World_Dark_Gray_Base` : `Canvas/World_Light_Gray_Base`), { maxNativeZoom: 16, maxZoom: 19, attribution: `© Esri` });
    sat.addTo(m);
    L.control.layers({ Satélite: sat, Mapa: base }, null, { position: `topright` }).addTo(m);
    const lim = [];
    for (const p of puntos) {
      if (p.lat == null) continue;
      const color = p.tipo === `COMEDOR` ? COLOR.parcial : COLOR.completo;
      const radio = L.circle([p.lat, p.lng], { radius: p.radioM, color, weight: 1, fillOpacity: 0.12 }).addTo(m);
      const icono = L.divIcon({ className: ``, html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`, iconSize: [18, 18], iconAnchor: [9, 9] });
      const mk = L.marker([p.lat, p.lng], { icon: icono, draggable: !!editable })
        .bindTooltip(`${p.codigo}`, { permanent: true, direction: `top`, offset: [0, -10], className: `lb-etiqueta-mapa` })
        .addTo(m);
      mk.on(`drag`, (e) => radio.setLatLng(e.target.getLatLng()));
      mk.on(`dragend`, async (e) => {
        const { lat, lng } = e.target.getLatLng();
        try {
          await api(`/labores/puntos`, { method: `POST`, body: JSON.stringify({ ...p, lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }) });
          setAviso(`${p.codigo} quedó en ${lat.toFixed(5)}, ${lng.toFixed(5)}.`);
          p.lat = Number(lat.toFixed(6));
          p.lng = Number(lng.toFixed(6));
        } catch (x) {
          setAviso(x.message);
          mk.setLatLng([p.lat, p.lng]);
          radio.setLatLng([p.lat, p.lng]);
        }
      });
      lim.push([p.lat, p.lng]);
    }
    if (lim.length) m.fitBounds(lim, { padding: [40, 40], maxZoom: 18 }); else m.setView([-14.0665, -75.7285], 15);
    return () => m.remove();
  }, [puntos, editable]);
  return (
    <div className="lb-mapa-caja">
      <div ref={caja} className="lb-mapa-puntos" />
      <div className="lb-leyenda">
        <span><i style={{ background: COLOR.completo }} />SS.HH.</span>
        <span><i style={{ background: COLOR.parcial }} />Comedor</span>
        <span>El círculo es el radio de tolerancia.</span>
        {aviso && <span className="lb-bien">{aviso}</span>}
      </div>
    </div>
  );
}
